import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { SliderButtons } from 'shared/components/ui/slider-buttons';
import { targetIsInput } from 'shared/utils/target-is-input';

import { useTaskDetailShortcut } from '../../hooks/use-task-detail-shortcut';
import { MetaViewOptions } from '../../types';

export type MetaContentTabsProps = {
  selected: MetaViewOptions;
  onChange: (view: MetaViewOptions) => void;
  hideInsights?: boolean;
};

export const MetaContentTabs: React.FC<MetaContentTabsProps> = ({
  selected,
  onChange,
  hideInsights,
}) => {
  const { t } = useTranslation();

  const optionGroups = useMemo(
    () =>
      [
        [
          hideInsights
            ? undefined
            : {
                label: t('dialogs.task-detail.meta.options.statistics.label'),
                value: MetaViewOptions.Statistics,
              },
          {
            label: t('dialogs.task-detail.meta.options.upcoming.label'),
            value: MetaViewOptions.Upcoming,
          },
          {
            label: t('dialogs.task-detail.meta.options.settings.label'),
            value: MetaViewOptions.Settings,
          },
        ].filter(Boolean),
      ] as React.ComponentProps<
        typeof SliderButtons<MetaViewOptions>
      >['optionGroups'],
    [hideInsights, t],
  );

  const viewKeyDownGenerator =
    (view: MetaViewOptions) => (e: KeyboardEvent) => {
      if (targetIsInput(e.target)) {
        return; // Don't do a thing
      }

      if (view === MetaViewOptions.Statistics && hideInsights) {
        return;
      }

      onChange(view);
    };

  useTaskDetailShortcut(
    ['KeyI'],
    viewKeyDownGenerator(MetaViewOptions.Statistics),
  );
  useTaskDetailShortcut(
    ['KeyU'],
    viewKeyDownGenerator(MetaViewOptions.Upcoming),
  );
  useTaskDetailShortcut(
    ['KeyS'],
    viewKeyDownGenerator(MetaViewOptions.Settings),
  );

  return (
    <SliderButtons
      optionGroups={optionGroups}
      onOption={onChange}
      value={selected}
      hasDarkBackground
      isFullWidth
    />
  );
};
